import * as fmt from '../../lib/format';
import { Role } from '../../types/auth';
import { StandardId } from '../compliance/types';

// The words on Home, written from the numbers. Plain and short; the numbers are written by lib/format.

/** "Good morning, Sam": by the viewer's clock, which is right for a greeting (it is not data). */
export function greeting(name: string | null | undefined, hour: number): string {
  const when = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const first = (name ?? '').trim().split(/\s+/)[0];
  return first ? `${when}, ${first}` : when;
}

/** What each standard's items are called when listed together on Home. */
const PLURAL: Record<StandardId, (n: number) => string> = {
  naming: (n) => (n === 1 ? 'ticket title to clear up' : 'ticket titles to clear up'),
  flow: (n) => (n === 1 ? 'move outside the usual owner' : 'moves outside the usual owner'),
  timelog: (n) => (n === 1 ? 'time entry or ticket to fix' : 'time entries or tickets to fix'),
  planning: (n) => (n === 1 ? 'ticket carried over often' : 'tickets carried over often'),
};

const ORDER: StandardId[] = ['naming', 'flow', 'timelog', 'planning'];

export function toFixHeadline(total: number): string {
  if (total === 0) return 'Nothing to fix';
  return total === 1 ? '1 thing to fix' : `${fmt.count(total)} things to fix`;
}

/** "5 ticket titles to clear up · 1 time entry or ticket to fix"; empty when there is nothing. */
export function toFixBreakdown(byStandard: Record<StandardId, number>): string {
  return ORDER.filter((s) => byStandard[s] > 0)
    .map((s) => `${fmt.count(byStandard[s])} ${PLURAL[s](byStandard[s])}`)
    .join(' · ');
}

export function hoursHeadline(loggedHours: number): string {
  return `${fmt.hours(loggedHours)} logged this month`;
}

/** The weekdays that are over with no time logged, named; "Every weekday so far has time logged." when there are none. */
export function noLogSentence(days: string[], count: number): string {
  if (count === 0) return 'Every weekday so far has time logged.';
  const named = days.map((d) => fmt.weekdayDay(d)).join(', ');
  const earlier = count - days.length;
  const tail = earlier > 0 ? ` and ${fmt.count(earlier)} earlier` : '';
  return `${count === 1 ? '1 weekday so far has' : `${fmt.count(count)} weekdays so far have`} no time logged: ${named}${tail}.`;
}

/** "3 goals to achieve" / "No goal is yours yet". */
export function achieveHeadline(total: number): string {
  if (total === 0) return 'No goal is yours yet';
  return total === 1 ? '1 goal to achieve' : `${fmt.count(total)} goals to achieve`;
}

/** One plain line under a goal: why it is the person's, and how far it has come. */
export function goalLine(g: { why: 'owner' | 'assigned'; pct: number | null; project_name: string | null; project_key: string | null }): string {
  const project = g.project_name ?? g.project_key ?? 'this project';
  const reason = g.why === 'owner' ? `You own ${project}` : `Assigned to you in ${project}`;
  return g.pct === null ? `${reason}. No tickets yet.` : `${reason}. ${fmt.percent(g.pct)} of its tickets are shipped or ready.`;
}

export function nextHeadline(total: number): string {
  if (total === 0) return 'Nothing of yours is open in a running sprint.';
  return total === 1 ? '1 open ticket of yours is in a running sprint.' : `${fmt.count(total)} open tickets of yours are in a running sprint.`;
}

export function doneHeadline(total: number): string {
  if (total === 0) return 'Nothing shipped yet';
  return total === 1 ? '1 ticket shipped' : `${fmt.count(total)} tickets shipped`;
}

export function quarterHeadline(label: string, overallPct: number | null): string {
  return overallPct === null ? `${label}: no goal tickets yet` : `${label}: ${fmt.percent(overallPct)} of goal tickets shipped or ready`;
}

export function checkpointSentence(reached: boolean, targetPct: number): string {
  return reached ? `The halfway check has passed (target ${fmt.percent(targetPct)}).` : `The halfway check (target ${fmt.percent(targetPct)}) is still ahead.`;
}

/** "33% of 15 tickets": a project's goal progress. */
export function goalProgress(pct: number | null, tickets: number): string {
  return pct === null ? 'No goal tickets yet' : `${fmt.percent(pct)} of ${fmt.counted(tickets, 'ticket')}`;
}

/** Where a project opens, for the role: the page that role may open that is about that project. */
export function projectPath(role: Role, key: string, page?: '/hygiene' | '/effort'): string {
  const k = encodeURIComponent(key);
  if (role === 'admin') return `/compliance?project_key=${k}`;
  if (role === 'leader') return `/planning?project_key=${k}`;
  return `${page ?? '/hygiene'}?project_key=${k}`;
}

export const personPath = (accountId: string): string => `/reports?person=${encodeURIComponent(accountId)}`;
